import { Injectable } from '@angular/core';

import { OptionsVerificationType, VerificationModel, VerificationInitModel, ClaimingBottomsheetText } from '../../../api/models/claiming.models';
import { TranslateService } from '@ngx-translate/core';
import { reOrderVerificationOptions } from '../../utils/claiming.utils';

@Injectable()
export class ClaimingStaticService {
  private readonly IMAGE_OPTION_MAP: Partial<Record<OptionsVerificationType, string>> = {
    [OptionsVerificationType.PHONE_CALL]: 'PHONE',
    [OptionsVerificationType.SMS]: 'SMS',
  };

  constructor(private readonly translate: TranslateService) {}

  public getBasicBottomSheetText(stage: any): ClaimingBottomsheetText {
    return {
      question: this.translate.instant(`csa.claiming.bottomsheet.interceptor.${stage}.title`),
      text: this.translate.instant(`csa.claiming.bottomsheet.interceptor.${stage}.text`),
      buttonText: this.translate.instant(`csa.claiming.bottomsheet.interceptor.${stage}.continue`),
    };
  }

  public getCheckStatusText(): ClaimingBottomsheetText {
    return {
      question: this.translate.instant('csa.claiming.GMB_AWAITING_OWNERSHIP_DECISION.title'),
      text: this.translate.instant('csa.claiming.GMB_AWAITING_OWNERSHIP_DECISION.description'),
      buttonText: this.translate.instant('csa.claiming.GMB_AWAITING_OWNERSHIP_DECISION.button'),
    };
  }

  public verificationOptionTranslation(option: OptionsVerificationType): {
    option: string;
    optionForImage: string;
  } {
    return {
      option: option === OptionsVerificationType.PHONE_CALL ? 'PHONE' : option,
      optionForImage: this.IMAGE_OPTION_MAP[option] ?? option,
    };
  }

  public getInitialVerificationOptions(availableOptions: OptionsVerificationType[]): VerificationInitModel {
    const buildOptions = reOrderVerificationOptions(availableOptions).map(option => {
      const translateOption = this.verificationOptionTranslation(option);
      return {
        buttonText: this.translate.instant(`csa.claiming.verification.${translateOption.option}`),
        advice: this.translate.instant(`csa.claiming.verification.${translateOption.option}.advice`),
        option,
        imageURL: `../../../../assets/images/icons/illustration_verify-via-${translateOption.optionForImage}.svg`,
      };
    });

    return {
      title: this.translate.instant('csa.claiming.verification.title'),
      subtitle: this.translate.instant('csa.claiming.verification.subtitle'),
      availableOptions: buildOptions,
    };
  }

  public getVerificationModelTranslations(selectedOption: OptionsVerificationType): VerificationModel {
    const translateOption = this.verificationOptionTranslation(selectedOption);
    return {
      VERIFY_BY: {
        title: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.title`),
        subtitle: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.subtitle`),
        options: {
          SMS: [
            {
              text: this.translate.instant('csa.claiming.verification.PHONE.page.buttons.sms'),
              option: OptionsVerificationType.SMS,
            },
          ],
          PHONE_CALL: [
            {
              text: this.translate.instant('csa.claiming.verification.PHONE.page.buttons.call'),
              option: OptionsVerificationType.PHONE_CALL,
            },
          ],
          EMAIL: [
            {
              text: this.translate.instant('csa.claiming.verification.EMAIL.page.button'),
              option: OptionsVerificationType.EMAIL,
            },
          ],
          ADDRESS: [
            {
              text: this.translate.instant('csa.claiming.verification.ADDRESS.page.button'),
              option: OptionsVerificationType.ADDRESS,
            },
          ],
        },
        alreadyHaveCodeButton: this.translate.instant('csa.claiming.verification.generic.already.have.code'),
        changeMethodMessage: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.no.access`),
        changeMethodButton: this.translate.instant('csa.claiming.verification.generic.choose.verification'),
      },
      PERFORM_VERIFY: {
        title: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.title`),
        subtitle: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.type.code`),
        confirmButton: this.translate.instant('csa.claiming.verification.generic.confirm'),
        wrongCodeText: this.translate.instant('csa.claiming.verification.phone.page.wrong'),
        codeNotReceivedButton: this.translate.instant('csa.claiming.verification.generic.not.received'),
      },
      CODE_ACCEPTED: {
        title: this.translate.instant('csa.claiming.verification.generic.accepted'),
        message: this.translate.instant('csa.claiming.verification.generic.next'),
        button: this.translate.instant('csa.claiming.verification.generic.thanks'),
        imageURL: '../../../../assets/images/icons/illustration_Verification-code-accepted.svg',
      },
      VERIFICATION_ERROR: {
        title: this.translate.instant('csa.claiming.verification.generic.error.title'),
        imageURL: '../../../../assets/images/icons/illustration_VERIFICATION_ERROR.svg',
        message: this.translate.instant('csa.claiming.verification.generic.error.message'),
        button: this.translate.instant('csa.claiming.verification.generic.thanks'),
      },
      MAXIMUM_ATTEMPTS: {
        title: this.translate.instant('csa.claiming.verification.generic.maximum.attempts.title'),
        imageURL: '../../../../assets/images/icons/illustration_max-no-of-attempts.svg',
        message: this.translate.instant('csa.claiming.verification.generic.maximum.attempts.message'),
        sendAgainButton: this.translate.instant('csa.claiming.verification.generic.send.again'),
        changeMethodMessage: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.no.access`),
        changeMethodButton: this.translate.instant('csa.claiming.verification.generic.choose.verification'),
      },
      SEND_AGAIN: {
        title: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.again`),
        subtitle: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.code.sent`),
        sendAgainButton: this.translate.instant('csa.claiming.verification.generic.send.again'),
        codeReceivedButton: this.translate.instant('csa.claiming.verification.generic.received'),
        changeMethodMessage: this.translate.instant(`csa.claiming.verification.${translateOption.option}.page.no.access`),
        changeMethodButton: this.translate.instant('csa.claiming.verification.generic.choose.verification'),
      },
      ON_ITS_WAY: {
        title: this.translate.instant('csa.claiming.verification.ADDRESS.page.code.sending'),
        imageURL: '../../../../assets/images/icons/illustration_ON_ITS_WAY.svg',
        message: this.translate.instant('csa.claiming.verification.ADDRESS.page.code.sent'),
        button: this.translate.instant('csa.claiming.verification.generic.thanks'),
      },
    };
  }
}
